/**
 * Safe Storage Utilities
 * Handles localStorage/sessionStorage with proper error handling
 * Gracefully handles quota exceeded, private browsing, and disabled storage
 */

/**
 * Safe localStorage wrapper
 */
export const safeLocalStorage = {
  /**
   * Set item in localStorage with error handling
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   * @returns {boolean} - Success status
   */
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded, attempting to clear old data');
        // Try to clear old data and retry
        try {
          clearOldStorageData();
          localStorage.setItem(key, value);
          return true;
        } catch (retryError) {
          console.error('Failed to save after clearing old data:', retryError);
          return false;
        }
      } else if (error.name === 'SecurityError' || error.code === 18) {
        // Private browsing mode or storage disabled
        console.warn('Storage access denied (private browsing or disabled):', error);
        return false;
      } else {
        console.error('Storage error:', error);
        return false;
      }
    }
  },

  /**
   * Get item from localStorage with error handling
   * @param {string} key - Storage key
   * @returns {string|null} - Stored value or null
   */
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Storage read error:', error);
      return null;
    }
  },

  /**
   * Remove item from localStorage with error handling
   * @param {string} key - Storage key
   * @returns {boolean} - Success status
   */
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Storage remove error:', error);
      return false;
    }
  },

  /**
   * Clear all localStorage with error handling
   * @returns {boolean} - Success status
   */
  clear: () => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.warn('Storage clear error:', error);
      return false;
    }
  },

  /**
   * Check if storage is available
   * @returns {boolean} - Storage availability
   */
  isAvailable: () => {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }
};

/**
 * Safe sessionStorage wrapper
 */
export const safeSessionStorage = {
  /**
   * Set item in sessionStorage with error handling
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   * @returns {boolean} - Success status
   */
  setItem: (key, value) => {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('Session storage quota exceeded');
        return false;
      } else if (error.name === 'SecurityError' || error.code === 18) {
        console.warn('Session storage access denied:', error);
        return false;
      } else {
        console.error('Session storage error:', error);
        return false;
      }
    }
  },

  /**
   * Get item from sessionStorage with error handling
   * @param {string} key - Storage key
   * @returns {string|null} - Stored value or null
   */
  getItem: (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.warn('Session storage read error:', error);
      return null;
    }
  },

  /**
   * Remove item from sessionStorage with error handling
   * @param {string} key - Storage key
   * @returns {boolean} - Success status
   */
  removeItem: (key) => {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Session storage remove error:', error);
      return false;
    }
  },

  /**
   * Clear all sessionStorage with error handling
   * @returns {boolean} - Success status
   */
  clear: () => {
    try {
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.warn('Session storage clear error:', error);
      return false;
    }
  },

  /**
   * Check if sessionStorage is available
   * @returns {boolean} - Storage availability
   */
  isAvailable: () => {
    try {
      const test = '__session_storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }
};

/**
 * Clear old storage data when quota is exceeded
 * Removes oldest entries based on timestamp
 */
const clearOldStorageData = () => {
  const keysToKeep = ['lastVisitedPage', 'recentPages', 'viewMode']; // Critical keys
  const allKeys = Object.keys(localStorage);
  
  // Remove non-critical keys first
  allKeys.forEach(key => {
    if (!keysToKeep.includes(key)) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        // Ignore errors during cleanup
      }
    }
  });
  
  // If still full, clear recentPages (less critical)
  if (safeLocalStorage.getItem('recentPages')) {
    safeLocalStorage.removeItem('recentPages');
  }
};

/**
 * Get storage usage info (for debugging)
 * @returns {object} - Storage usage information
 */
export const getStorageInfo = () => {
  let localStorageSize = 0;
  let sessionStorageSize = 0;
  
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        localStorageSize += value.length + key.length;
      }
    });
  } catch (error) {
    // Ignore
  }
  
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value) {
        sessionStorageSize += value.length + key.length;
      }
    });
  } catch (error) {
    // Ignore
  }
  
  return {
    localStorage: {
      size: localStorageSize,
      sizeKB: (localStorageSize / 1024).toFixed(2),
      available: safeLocalStorage.isAvailable()
    },
    sessionStorage: {
      size: sessionStorageSize,
      sizeKB: (sessionStorageSize / 1024).toFixed(2),
      available: safeSessionStorage.isAvailable()
    }
  };
};

